import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { Card } from '../Card';
import { ThemedText } from '..';
import { ToggleSwitch, ToggleInput, ToggleSlider } from '../ToggleStyles';
import organizationService from '../../api/services/organizationService';
import { invalidateOrganizationConfig } from '../../hooks/useOrganizationConfig';

const Row = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${props => props.theme.spacing.lg};
  flex-wrap: wrap;
`;

const Copy = styled.div`
  flex: 1;
  min-width: 260px;
`;

const SubGroup = styled.div`
  margin-top: ${props => props.theme.spacing.lg};
  padding-top: ${props => props.theme.spacing.lg};
  border-top: 1px solid ${props => props.theme.colors.border};
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

// The metadata-edit flags shown grouped under the main portal-submission toggle.
const META_FLAGS = [
  {
    key: 'allowEditSubmissionLocation',
    title: 'Allow editing the location',
    description:
      'When enabled, members can adjust the map pin (GPS) when creating or editing a submission, instead of using only the auto-captured location.',
  },
  {
    key: 'allowEditSubmissionDateTime',
    title: 'Allow editing the date & time',
    description:
      'When enabled, members can set or change a submission’s date and time. When disabled, it is fixed to the moment of submission.',
  },
  {
    key: 'allowEditSubmissionSubmitter',
    title: 'Allow editing the submitter (admins only)',
    description:
      'When enabled, organization admins can change the submitter name and email on a submission. Regular members can never change the submitter.',
  },
];

const PortalSubmissionSetting = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const orgId = user?.organizationId ?? null;

  const [flags, setFlags] = useState({
    enablePortalFormSubmission: true,
    allowEditSubmissionLocation: false,
    allowEditSubmissionDateTime: false,
    allowEditSubmissionSubmitter: false,
  });
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    if (orgId == null) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    organizationService
      .getOrganizationById(orgId)
      .then((org) => {
        if (active) {
          setFlags({
            enablePortalFormSubmission: org?.enablePortalFormSubmission !== false,
            allowEditSubmissionLocation: org?.allowEditSubmissionLocation === true,
            allowEditSubmissionDateTime: org?.allowEditSubmissionDateTime === true,
            allowEditSubmissionSubmitter: org?.allowEditSubmissionSubmitter === true,
          });
          setLoading(false);
        }
      })
      .catch(() => {
        if (active) {
          setError('Failed to load organization settings.');
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [orgId]);

  const handleToggle = async (key, successCopy) => {
    if (savingKey || loading || orgId == null) return;
    const next = !flags[key];
    setFlags((prev) => ({ ...prev, [key]: next }));
    setSavingKey(key);
    setError(null);
    try {
      await organizationService.updateOrganization(orgId, { [key]: next });
      invalidateOrganizationConfig(orgId);
      toast.success(successCopy(next));
    } catch (err) {
      setFlags((prev) => ({ ...prev, [key]: !next })); // revert on failure
      setError(err?.message || 'Failed to update setting.');
      toast.error('Failed to update setting.');
    } finally {
      setSavingKey(null);
    }
  };

  if (orgId == null) {
    return (
      <Card theme={theme} style={{ marginBottom: theme.spacing.lg }}>
        <ThemedText type="subtitle" style={{ marginBottom: theme.spacing.xs }}>
          Form Submission via Portal
        </ThemedText>
        <ThemedText type="small" style={{ color: theme.colors.muted }}>
          This setting applies per organization. Manage it for each organization from the Organizations page.
        </ThemedText>
      </Card>
    );
  }

  const renderToggle = (key, checked, onChange) => (
    <ToggleSwitch theme={theme} style={{ opacity: loading || savingKey ? 0.6 : 1 }}>
      <ToggleInput
        type="checkbox"
        checked={checked}
        disabled={loading || savingKey != null}
        onChange={onChange}
      />
      <ToggleSlider theme={theme} $checked={checked} />
      <span style={{ fontWeight: 600, color: theme.colors.text }}>
        {checked ? 'Enabled' : 'Disabled'}
      </span>
    </ToggleSwitch>
  );

  return (
    <Card theme={theme} style={{ marginBottom: theme.spacing.lg }}>
      <Row theme={theme}>
        <Copy>
          <ThemedText type="subtitle" style={{ marginBottom: theme.spacing.xs }}>
            Form Submission via Portal
          </ThemedText>
          <ThemedText type="small" style={{ color: theme.colors.muted }}>
            When enabled, members can create form submissions from the web portal. When disabled,
            submissions can only be collected from the mobile app.
          </ThemedText>
        </Copy>
        {renderToggle(
          'enablePortalFormSubmission',
          flags.enablePortalFormSubmission,
          () =>
            handleToggle('enablePortalFormSubmission', (next) =>
              next ? 'Portal form submission enabled.' : 'Portal form submission disabled.'
            )
        )}
      </Row>

      <SubGroup theme={theme}>
        <ThemedText type="small" style={{ color: theme.colors.muted, fontWeight: 600 }}>
          Editable submission metadata
        </ThemedText>
        {META_FLAGS.map(({ key, title, description }) => (
          <Row theme={theme} key={key}>
            <Copy>
              <ThemedText type="body" style={{ fontWeight: 600, marginBottom: theme.spacing.xs }}>
                {title}
              </ThemedText>
              <ThemedText type="small" style={{ color: theme.colors.muted }}>
                {description}
              </ThemedText>
            </Copy>
            {renderToggle(key, flags[key], () =>
              handleToggle(key, (next) => `${title.replace(' (admins only)', '')} ${next ? 'enabled' : 'disabled'}.`)
            )}
          </Row>
        ))}
        {error && (
          <ThemedText style={{ color: theme.colors.danger }}>{error}</ThemedText>
        )}
      </SubGroup>
    </Card>
  );
};

export default PortalSubmissionSetting;
